import { cn, formatDateValue } from "@/lib/utils";
import { DataService } from "@/services/data";
import { Interaction, InteractionFilter, RelatedObjectFormType } from "@/types/dynamic-detail";
import * as LucideIcons from "lucide-react";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageDialog } from "../tools/message/dialog";
import { Button } from "../ui/button";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { DynamicObjectForm } from "./dynamic-object-form";

const OBJECT_TYPE_LABELS: Record<string, string> = {
    opportunities: "Opportunity",
    cases: "Case"
};

interface InteractionsHistoryProps {
    interactions: Interaction[];
    className?: string;
    /** Backend-declared write surface: absent, the timeline stays read-only. */
    noteForm?: RelatedObjectFormType | null;
    /** Record a new note hangs from, filling `noteForm.parent_field`. */
    objectId?: string | number;
    objectType?: string;
    count?: number;
    next?: number | null;
    filters?: InteractionFilter[];
    canViewAccountInteractions?: boolean;
}

/** Calls placed by a bot rather than by a salesperson. */
const AUTOMATED_CALL_TYPES = ["Automated Outbound Call", "Automated Inbound Call"];

/**
 * Call types showing the campaign, the talk duration and the qualification. An automated
 * call reads exactly like a human one here: the robot's summary lives on the call record
 * it links to, and standing in for those three facts made a bot call unreadable next to
 * the human calls it is compared against.
 */
const CALL_METADATA_TYPES = ["Outbound Call", "Inbound Call", ...AUTOMATED_CALL_TYPES];

function categorizeInteraction(interaction: Interaction): string {
    const interactionDate = DateTime.fromISO(interaction.interaction_date);
    if (!interactionDate.isValid) return "Older";
    const now = DateTime.now();
    if (interactionDate > now) return "Upcoming";
    if (interactionDate.hasSame(now, "day")) return "Today";
    const yesterday = now.minus({ days: 1 });
    if (interactionDate.hasSame(yesterday, "day")) return "Yesterday";
    const lastWeek = now.minus({ days: 7 });
    if (interactionDate >= lastWeek) return "Last 7 days";
    const lastMonth = now.minus({ days: 30 });
    if (interactionDate >= lastMonth) return "Last 30 days";
    return "More than 30 days ago";
}

export function InteractionsHistory(props: InteractionsHistoryProps) {
    const categoryOrder = ["Upcoming", "Today", "Yesterday", "Last 7 days", "Last 30 days", "More than 30 days ago"];
    const [interactions, setInteractions] = useState(props.interactions);
    const [count, setCount] = useState(props.count ?? props.interactions.length);
    const [nextPage, setNextPage] = useState(props.next ?? null);
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
    const [viewAllAccount, setViewAllAccount] = useState(false);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        setInteractions(props.interactions);
        setCount(props.count ?? props.interactions.length);
        setNextPage(props.next ?? null);
    }, [props.interactions, props.count, props.next]);

    useEffect(() => {
        setSelectedTypes(new Set());
        setViewAllAccount(false);
    }, [props.objectId, props.objectType]);

    const fetchPage = async (
        page: number,
        append: boolean,
        types: Set<string> = selectedTypes,
        accountWide: boolean = viewAllAccount
    ) => {
        if (!props.objectType || !props.objectId) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const data = await DataService.client
                .url(`/crm/objects/${props.objectType}/${props.objectId}/interaction-history/`)
                .query({
                    page,
                    page_size: 5,
                    types: [...types].join(","),
                    view_all_account: accountWide
                })
                .get()
                .json<{ interactions: Interaction[]; count: number; next: number | null }>();
            if (requestId !== requestIdRef.current) return;
            setInteractions(current => (append ? [...current, ...data.interactions] : data.interactions));
            setCount(data.count);
            setNextPage(data.next);
        } catch (error) {
            console.error("Error loading interactions history:", error);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    const mappedInteractions = [...interactions].sort((a, b) => DateTime.fromISO(b.interaction_date).toMillis() - DateTime.fromISO(a.interaction_date).toMillis());
    const groupedInteractions: { [key: string]: Interaction[] } = {};
    categoryOrder.forEach(category => {
        groupedInteractions[category] = [];
    });
    mappedInteractions.forEach(interaction => {
        const category = categorizeInteraction(interaction);
        groupedInteractions[category].push(interaction);
    });

    // Helper pour rendre dynamiquement l'icône Lucide
    function renderLucideIcon(icon: string, className: string = "") {
        const LucideIcon = (LucideIcons as any)[icon] || LucideIcons["CircleHelp"];
        return <LucideIcon className={className} />;
    }

    const [openMessageDialog, setOpenMessageDialog] = useState(false);
    const [messagePhoneNumber, setMessagePhoneNumber] = useState<string | undefined>(undefined);
    const [openNoteDialog, setOpenNoteDialog] = useState(false);

    // A note is the one interaction written rather than recorded, so its button belongs on the
    // timeline it will appear in. An empty timeline still shows it: that is where the first
    // note is created from.
    const noteForm = props.noteForm && props.objectId ? props.noteForm : null;

    return (
        <div className={cn("relative flex w-full flex-col", props.className)}>
            <div className="flex flex-wrap items-start justify-between gap-2 pb-2 text-md font-bold">
                    <div className="flex flex-row items-center gap-2">
                         <div className={cn("flex h-6 w-6 items-center justify-center rounded-sm p-1")} style={{ backgroundColor: "#007AFF" }}>
                             <LucideIcons.RotateCcw className="text-white" />
                        </div>
                        Notes & Interactions ({count}
                        {count > interactions.length ? "+" : ""}{viewAllAccount && " incl. all Account interactions"})
                    </div>
                    <div className="flex items-center gap-2">
                         {noteForm?.permissions.create && (
                    <Button size="sm" variant="outline" onClick={() => setOpenNoteDialog(true)}>
                        <LucideIcons.Plus className="mr-1 h-4 w-4" />
                        {noteForm.config.create_label}
                    </Button>
                )}
                    </div>
            </div>
             <div className="flex flex-wrap items-center gap-2 justify-end my-2">
                    {props.canViewAccountInteractions && (
                        <div
                            className="cursor-pointer text-sm text-azure-blue hover:underline underline-offset-4 flex items-center gap-2"
                            onClick={() => {
                                const next = !viewAllAccount;
                                setViewAllAccount(next);
                                void fetchPage(1, false, selectedTypes, next);
                            }}
                        >
                        {viewAllAccount ?<LucideIcons.EyeOff className=" h-4 w-4 inline-block" /> : <LucideIcons.Eye className=" h-4 w-4 inline-block" />}
                            {viewAllAccount
                                ? `Restrict to ${OBJECT_TYPE_LABELS[props.objectType ?? ""] ?? "record"} interactions only`
                                : "Show all Account interactions"}
                        </div>
                    )}
                    {props.filters && props.filters.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div className="cursor-pointer flex items-center gap-2 text-sm text-azure-blue hover:underline underline-offset-4">
                                    <LucideIcons.ListFilter className=" h-4 w-4 inline-block" />
                                    Filter
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                {props.filters.map(filter => (
                                    <DropdownMenuCheckboxItem
                                        key={filter.value}
                                        checked={selectedTypes.has(filter.value)}
                                        onCheckedChange={checked => {
                                            const next = new Set(selectedTypes);
                                            if (checked) next.add(filter.value);
                                            else next.delete(filter.value);
                                            setSelectedTypes(next);
                                            void fetchPage(1, false, next);
                                        }}
                                    >
                                        {filter.label}
                                    </DropdownMenuCheckboxItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            {interactions.length === 0 && !loading && <div className="mt-4 text-sm text-gray-500">No interactions history available</div>}
            {categoryOrder.map((categoryName, index) => {
                const interactions = groupedInteractions[categoryName];
                if (interactions.length === 0) return null;
                return (
                    <div key={index} className="w-full">
                        <div className="flex w-full items-center gap-2 rounded bg-gray-100 px-5 py-1.5">
                            <span className="text-sm font-normal text-black">{categoryName}</span>
                        </div>
                        <div className="flex flex-col gap-4 bg-white px-2 py-2">
                            {interactions.map((interaction, interactionIndex) => {
                                const isCancelled = Boolean(interaction.is_cancelled);
                                const isAutomatedCall = AUTOMATED_CALL_TYPES.includes(interaction.type);
                                const showsCallMetadata = CALL_METADATA_TYPES.includes(interaction.type);
                                const content = (
                                    <div
                                        className={cn(
                                            "group relative flex flex-row items-start gap-2 py-2 last:mb-0",
                                            isCancelled && "opacity-40 grayscale",
                                            !isCancelled && interaction.archived && "opacity-60"
                                        )}
                                    >
                                        <div
                                            className={`z-10 mr-2 flex aspect-square h-7 w-7 flex-col items-center justify-center rounded`}
                                            style={{
                                                backgroundColor: isCancelled ? "#1a1a1a" : interaction.icon_bg,
                                                color: interaction.icon_fg
                                            }}
                                        >
                                            {renderLucideIcon(interaction.icon, "mx-auto h-4 w-4 text-center text-white")}
                                        </div>
                                        {interactionIndex !== interactions.length - 1 && <div className="absolute left-3 top-9 -z-0 h-[calc(100%-11px)] w-0.5 bg-gray-200"></div>}
                                        <div className="flex w-full justify-between gap-2">
                                            <div className="flex w-full flex-col">
                                                <div className="flex flex-col justify-between sm:flex-row sm:gap-2">
                                                    <span className={cn("flex items-center truncate text-ellipsis text-sm", interaction.links.main && "group-hover:underline")}>
                                                        {isCancelled && (
                                                            <span className="mr-2 inline-flex shrink-0 items-center rounded bg-neutral-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                                                                Cancelled
                                                            </span>
                                                        )}
                                                        <span className={cn("truncate", isCancelled && "text-neutral-700 line-through")}>
                                                            {interaction.subject ? (interaction.subject.length > 50 ? interaction.subject.slice(0, 50) + "..." : interaction.subject) : "No subject"}
                                                        </span>
                                                        <span className="truncate whitespace-pre">
                                                            {interaction.type === "Inbound Call" || interaction.type === "Missed Inbound Call" ? " to " : " by "}
                                                        </span>
                                                        {interaction.links && interaction.links.owner && (
                                                            <Link to={interaction.links.owner} className="text-sm text-azure-blue">
                                                                <>
                                                                    <span className="hidden whitespace-nowrap lg:inline">
                                                                        {interaction.owner?.first_name} {interaction.owner?.last_name}
                                                                    </span>
                                                                    <span className="inline whitespace-nowrap lg:hidden">
                                                                        {interaction.owner?.first_name} {interaction.owner?.last_name[0]}.
                                                                    </span>
                                                                </>
                                                            </Link>
                                                        )}
                                                    </span>
                                                    <span className={cn("flex text-sm", isCancelled ? "text-neutral-600" : "text-gray-500")}>
                                                        {formatDateValue(DateTime.fromISO(interaction.interaction_date), "long")}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col">
                                                    {showsCallMetadata && (
                                                        <div className="flex flex-col sm:flex-row sm:gap-10">
                                                            {interaction.campaign && (
                                                                <div className="col-span-1 truncate text-sm text-gray-500">
                                                                    {renderLucideIcon("Component", "mb-0.5 mr-1 inline-block h-4 w-4")}
                                                                    {interaction.campaign}
                                                                </div>
                                                            )}
                                                            <div className="col-span-1 truncate text-sm text-gray-500">
                                                                {renderLucideIcon("Clock4", "mb-0.5 mr-1 inline-block h-4 w-4")}
                                                                {interaction.duration}
                                                            </div>
                                                            {interaction.outcome && (
                                                                <div className="flex flex-row gap-10">
                                                                    <div className="col-span-1 truncate text-sm text-gray-500">
                                                                        {renderLucideIcon("Goal", "mb-0.5 mr-1 inline-block h-4 w-4")}
                                                                        {interaction.outcome}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {interaction.callback_date && (
                                                                <div className="flex flex-row gap-10">
                                                                    <div className="col-span-1 truncate text-sm text-gray-500">
                                                                        {renderLucideIcon("CalendarClock", "mb-0.5 mr-1 inline-block h-4 w-4")}
                                                                        {DateTime.fromISO(interaction.callback_date).toLocaleString(DateTime.DATETIME_SHORT)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {!isAutomatedCall &&
                                                        interaction.description &&
                                                        interaction.description !== "" &&
                                                        interaction.description !== "None" &&
                                                        interaction.description !== "Missed call None" &&
                                                        interaction.description !== "Missed Call None" && (
                                                            <span className={cn("col-span-3 text-sm", isCancelled ? "text-neutral-600" : "text-gray-500")}>
                                                                {renderLucideIcon("Text", "mb-0.5 mr-1 inline-block h-4 w-4")}
                                                                {interaction.description}
                                                            </span>
                                                        )}
                                                    {interaction.reply_action === true && interaction.location && (
                                                        <Button
                                                            variant="outline"
                                                            className="mt-2 w-fit text-sm text-gray-800"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                if (interaction.location) {
                                                                    setMessagePhoneNumber(interaction.location);
                                                                    setOpenMessageDialog(true);
                                                                }
                                                            }}
                                                        >
                                                            {renderLucideIcon("Reply", "mb-0.5 mr-1 inline-block h-4 w-4")}
                                                            Reply
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );

                                return <React.Fragment key={interactionIndex}>{interaction.links.main ? <Link to={interaction.links.main}>{content}</Link> : content}</React.Fragment>;
                            })}
                        </div>
                    </div>
                );
            })}
            {nextPage && (
                <div className="mt-2 flex items-center justify-center gap-2">
                    <button
                        className="rounded bg-gray-100 px-4 py-1 text-sm text-primary hover:bg-gray-200 disabled:opacity-50"
                        disabled={loading}
                        onClick={() => void fetchPage(nextPage, true)}
                    >
                        {loading && <LucideIcons.Loader className="mr-1 h-4 w-4 animate-spin" />}
                        See more
                    </button>
                </div>
            )}
            <MessageDialog phone_number={messagePhoneNumber} open={openMessageDialog} setOpen={setOpenMessageDialog} />
            {noteForm && (
                <DynamicObjectForm
                    open={openNoteDialog}
                    onOpenChange={setOpenNoteDialog}
                    mode="create"
                    formConfig={noteForm.config}
                    endpointUrl={`/crm/objects/${noteForm.child_object_url}/`}
                    formIndex={noteForm.form_index}
                    formScope="list"
                    fixedValues={{ [noteForm.parent_field]: props.objectId }}
                    onSuccess={() => void fetchPage(1, false)}
                />
            )}
        </div>
    );
}
